import { useTranslation } from 'react-i18next';

import { INGREDIENTS } from '@/lib/gameData';
import { playSound, unlock } from '@/lib/sound';
import { hapticImpact } from '@/lib/telegram';
import { useCafe3dStore } from '@/stores/cafe3dStore';
import type { IngredientId } from '@/types/game-3d';

// Phase 1 + 2: 8 ingredients đồng bộ với backend master data (migration 0009).
// Khách Phase 2 có thể order trà/cà phê muối → cần đủ nguyên liệu để phục vụ.
const ALL_INGREDIENTS: IngredientId[] = [
  'coffee', 'milk', 'ice', 'sugar',     // Phase 1
  'tea', 'lemon', 'peach', 'salt',      // Phase 2
];

export function IngredientBar() {
  const { t } = useTranslation();
  const addIngredient = useCafe3dStore((s) => s.addIngredient);
  const sceneState = useCafe3dStore((s) => s.sceneState);

  const ingredients = ALL_INGREDIENTS;
  const disabled = sceneState !== 'ordering';

  return (
    <div className="px-3 py-3 bg-cream-50/95 border-t border-coffee-700/10 game-ui">
      {/* justify-start + flex-nowrap để item đầu (Cà phê) luôn hiện từ mép trái khi
          danh sách 8 nguyên liệu vượt width container (mobile). User scroll ngang
          để xem các item sau. */}
      <div className="flex gap-2 justify-start flex-nowrap overflow-x-auto no-scrollbar snap-x snap-mandatory">
        {ingredients.map((id) => {
          const ing = INGREDIENTS[id];
          return (
            <button
              key={id}
              onClick={() => {
                unlock();
                playSound("ingredient_click");
                hapticImpact("light");
                addIngredient(id);
              }}
              disabled={disabled}
              className={`
                flex-shrink-0 w-14 h-[72px] rounded-2xl border-2 transition snap-start
                flex flex-col items-center justify-center gap-1
                ${disabled
                  ? 'opacity-40 bg-cream-100 border-coffee-700/10'
                  : 'bg-cream-100 border-coffee-700/20 hover:border-yellow-light hover:bg-yellow-light/20 active:scale-90'
                }
              `}
            >
              <span className="text-2xl">{ing.emoji}</span>
              <span className="text-[10px] font-medium text-coffee-900 leading-none">
                {t(`ingredient.${id}`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CurrentMixAndServe() {
  const { t } = useTranslation();
  const currentMix = useCafe3dStore((s) => s.currentMix);
  const clearMix = useCafe3dStore((s) => s.clearMix);
  const serveCurrentOrder = useCafe3dStore((s) => s.serveCurrentOrder);
  const sceneState = useCafe3dStore((s) => s.sceneState);

  const canServe = currentMix.length > 0 && sceneState === 'ordering';

  return (
    <div className="px-3 py-2 bg-cream-100/95 border-t border-coffee-700/10 game-ui flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-coffee-700 uppercase tracking-wide">
          {t('gameplay.mixing')}
        </div>
        <div className="flex items-center gap-1 mt-0.5 min-h-[24px]">
          {currentMix.length === 0 ? (
            <span className="text-xs text-coffee-700/50 italic">
              {t('gameplay.pick_ingredients')}
            </span>
          ) : (
            currentMix.map((id, i) => (
              <span key={i} className="text-lg">
                {INGREDIENTS[id].emoji}
                {i < currentMix.length - 1 && <span className="text-coffee-700/40 mx-0.5">+</span>}
              </span>
            ))
          )}
        </div>
      </div>
      {currentMix.length > 0 && (
        <button
          onClick={clearMix}
          className="px-2 py-1 text-xs text-coffee-700 active:scale-95"
        >
          {t('gameplay.clear_button')}
        </button>
      )}
      <ServeButton
        onClick={() => {
          if (!canServe) return;
          unlock();
          playSound("serve_success");
          hapticImpact("medium");
          setTimeout(() => playSound("revenue_ting"), 250);
          serveCurrentOrder();
        }}
        disabled={!canServe}
      />
    </div>
  );
}

function ServeButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative px-5 py-3 rounded-xl font-baloo font-semibold text-base
        transition transform
        ${disabled
          ? 'bg-coffee-700/30 text-cream-50/50 cursor-not-allowed'
          : 'bg-red-stool text-cream-50 shadow-[0_4px_0_#8E2926] hover:translate-y-0.5 hover:shadow-[0_2px_0_#8E2926] active:translate-y-1 active:shadow-[0_0px_0_#8E2926]'
        }
      `}
    >
      {t('gameplay.serve_button')}
    </button>
  );
}
